import styles from './HoverCard.module.scss';
import cn from "classnames";
import useOutsideClick from "@hooks/useOutsideClick";
import { useState, useRef, useCallback } from "react";

export interface IHoverCardAction {
  isActive: boolean;
}

export interface IHoverCardContent {
  close: () => void;
}

export interface IHoverCardProps {
  Action: React.FunctionComponent<IHoverCardAction>;
  Content: React.FunctionComponent<IHoverCardContent>;
  position?: "top" | "left" | "right" | "bottom";
  align?: "start" | "end" | "center";
  disabled?: boolean;
  trigger?: "click" | "hover";
}

export const HoverCard: React.FC<IHoverCardProps> = (props: IHoverCardProps): React.ReactElement => {
  const { position, align, Action, Content, disabled } = props;
  const trigger = props.trigger ?? "hover";
  const [isVisible, setIsVisible] = useState(false);
  const isFocused = useRef(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const hideCard = useCallback(() => setIsVisible(false), [setIsVisible])

  useOutsideClick<HTMLDivElement>(dropdownRef, hideCard);

  const onMouseLeave = () => {
    if (trigger === "hover") {
      isFocused.current = false;
      setTimeout(() => {
        if (!isFocused.current) setIsVisible(false)
      }, 200);
    }
  }

  return (
    <div className={styles.root}>
      <div
        className={styles.action}
        onMouseEnter={() => {
          if (trigger === "hover") {
            isFocused.current = true;
            setIsVisible(true);
          }
        }}
        onMouseLeave={onMouseLeave}
        onClick={() => {
          if (trigger === "click") setIsVisible(v => !v)
        }}
      >
        <Action isActive={isVisible} />
      </div>
      {!disabled ? (
        <div
          ref={dropdownRef}
          onMouseEnter={() => {
            isFocused.current = true;
          }}
          onMouseLeave={onMouseLeave}
          className={cn(
            styles.hoverCard,
            isVisible && styles.hoverCardVisible,
            styles[`position-${position ?? "bottom"}`],
            styles[`align-${align ?? "center"}`],
          )}
        >
          <Content close={hideCard} />
        </div>
      ) : null}
    </div>
  );
};
